import { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Link2, Copy, Trash2, Plus, Check } from 'lucide-react'

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000

type AttachmentLike = {
    _id: Id<'prescription_attachments'>
    file_id: Id<'_storage'>
    filename: string
    content_type: string
    file_size: number
}

interface Props {
    prescriptionId: Id<'prescriptions'>
    attachment: AttachmentLike
    onClose: () => void
    /** Nome paziente dalla prescrizione (preset) */
    defaultPatientName?: string
    /** Numero prescrizione (preset) */
    defaultPrescriptionNumber?: string
}

export function SharedLinkDialog({
    prescriptionId,
    attachment,
    onClose,
    defaultPatientName,
    defaultPrescriptionNumber,
}: Props) {
    const links = useQuery(api.sharedLinks.listByPrescription, {
        prescription_id: prescriptionId,
    })
    const linksForFile = useMemo(
        () => (links ?? []).filter((l: any) => l.file_id === attachment.file_id),
        [links, attachment.file_id]
    )

    const createSharedLink = useMutation(api.sharedLinks.createSharedLink)
    const revokeSharedLink = useMutation(api.sharedLinks.revokeSharedLink)

    const [patientName, setPatientName] = useState<string>(defaultPatientName ?? '')
    const [prescriptionNumber, setPrescriptionNumber] = useState<string>(
        defaultPrescriptionNumber ?? ''
    )
    const [maxDownloadsText, setMaxDownloadsText] = useState<string>('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [copiedToken, setCopiedToken] = useState<string | null>(null)

    const handleCreate = async () => {
        setError(null)

        if (!patientName.trim()) {
            setError('Nome paziente obbligatorio')
            return
        }
        if (!prescriptionNumber.trim()) {
            setError('Numero prescrizione obbligatorio')
            return
        }

        const maxDownloads = maxDownloadsText.trim()
            ? parseInt(maxDownloadsText, 10)
            : undefined
        if (
            maxDownloads !== undefined &&
            (!Number.isFinite(maxDownloads) || maxDownloads <= 0)
        ) {
            setError('Numero download non valido')
            return
        }

        setSubmitting(true)
        try {
            await createSharedLink({
                file_id: attachment.file_id,
                filename: attachment.filename,
                content_type: attachment.content_type,
                file_size: attachment.file_size,
                prescription_id: prescriptionId,
                attachment_id: attachment._id,
                public_auth0: true,
                allowed_emails: undefined,
                expires_at: Date.now() + ONE_YEAR_MS,
                max_downloads: maxDownloads,
                notify_creator_on_access: false,
                patient_name: patientName.trim(),
                prescription_number_ref: prescriptionNumber.trim(),
                label: `${patientName.trim()} — n. ${prescriptionNumber.trim()}`,
            })
            setMaxDownloadsText('')
        } catch (err: any) {
            setError(err?.message ?? 'Errore durante la creazione del link')
        } finally {
            setSubmitting(false)
        }
    }

    const handleRevoke = async (linkId: Id<'shared_file_links'>) => {
        if (!confirm("Revocare questo link? L'operazione è immediata.")) return
        try {
            await revokeSharedLink({ link_id: linkId })
        } catch (err: any) {
            setError(err?.message ?? 'Errore durante la revoca')
        }
    }

    const buildShareUrl = (token: string) =>
        `${window.location.origin}/share/${token}`

    const handleCopy = async (token: string) => {
        try {
            await navigator.clipboard.writeText(buildShareUrl(token))
            setCopiedToken(token)
            setTimeout(() => setCopiedToken(null), 1500)
        } catch {
            // ignore
        }
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
                <div className="p-5 border-b flex items-center justify-between">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Link2 className="w-5 h-5" />
                        Link condivisi per: {attachment.filename}
                    </h2>
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Chiudi
                    </Button>
                </div>

                <div className="p-5 space-y-6">
                    {/* Lista link esistenti */}
                    <section>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                            Link esistenti ({linksForFile.length})
                        </h3>
                        {linksForFile.length === 0 ? (
                            <div className="text-sm text-gray-500 bg-gray-50 rounded p-3">
                                Nessun link generato per questo file.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {linksForFile.map((l: any) => {
                                    const status = l.revoked
                                        ? 'Revocato'
                                        : l.expires_at && l.expires_at < Date.now()
                                            ? 'Scaduto'
                                            : 'Attivo'
                                    const exp = l.expires_at
                                        ? new Date(l.expires_at).toLocaleDateString('it-IT')
                                        : 'permanente'
                                    return (
                                        <div
                                            key={l._id}
                                            className="border rounded p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                                        >
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">
                                                    {l.label || buildShareUrl(l.token)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {status} · scade: {exp} · download: {l.download_count}
                                                    {l.max_downloads ? `/${l.max_downloads}` : ''}
                                                </div>
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleCopy(l.token)}
                                                    disabled={l.revoked}
                                                >
                                                    {copiedToken === l.token ? (
                                                        <>
                                                            <Check className="w-3 h-3 mr-1" /> Copiato
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy className="w-3 h-3 mr-1" /> Copia
                                                        </>
                                                    )}
                                                </Button>
                                                {!l.revoked && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleRevoke(l._id)}
                                                    >
                                                        <Trash2 className="w-3 h-3 mr-1" />
                                                        Revoca
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </section>

                    {/* Form creazione */}
                    <section className="border-t pt-5 space-y-3">
                        <h3 className="text-sm font-medium text-gray-700">Genera nuovo link</h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                    Nome paziente <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    value={patientName}
                                    onChange={(e) => setPatientName(e.target.value)}
                                    placeholder="es. Mario Rossi"
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                    Numero prescrizione <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    value={prescriptionNumber}
                                    onChange={(e) => setPrescriptionNumber(e.target.value)}
                                    placeholder="es. 2025-00123"
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Scadenza</label>
                                <div className="h-10 flex items-center px-3 rounded-md border border-input bg-gray-50 text-sm text-gray-700">
                                    1 anno (fissa)
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                    Max download (opzionale)
                                </label>
                                <Input
                                    type="number"
                                    min={1}
                                    value={maxDownloadsText}
                                    onChange={(e) => setMaxDownloadsText(e.target.value)}
                                    placeholder="illimitato"
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <div className="text-xs text-gray-500 bg-gray-50 border rounded p-2">
                            Accesso: chiunque con un account Auth0 valido potrà aprire il link dopo
                            il login.
                        </div>

                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                                {error}
                            </div>
                        )}

                        <Button onClick={handleCreate} disabled={submitting} className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            {submitting ? 'Generazione...' : 'Genera link'}
                        </Button>
                    </section>
                </div>
            </div>
        </div>
    )
}
